import React, { useContext } from "react";
import { motion } from "framer-motion";
import { CursorContext } from "../context/CursorContext";
import { transition1 } from "../transitions";
import { ScrollLink } from "../components/ScrollLink";
import { BsArrowRight } from "react-icons/bs";
import { skillGroups } from "../Data";

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" },
  }),
};

const Skills = () => {
  const { mouseEnterHandler, mouseLeaveHandler } = useContext(CursorContext);

  return (
    <motion.section
      id="skills"
      className="section snap-start min-h-screen flex flex-col lg:pt-36 pt-24 pb-20 overflow-y-auto"
      initial={{ opacity: 0, y: "5%" }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: false, amount: 0.15 }}
      exit={{ opacity: 0, y: "5%" }}
      transition={transition1}
      onMouseEnter={mouseEnterHandler}
      onMouseLeave={mouseLeaveHandler}
    >
      <div className="container mx-auto px-6 flex flex-col h-full max-w-5xl">
        {/* ── Header ── */}
        <motion.div
          className="mb-6"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.2 }}
        >
          <motion.h1 className="h1 font-secondary" variants={fadeUp} custom={0}>
            Skills
          </motion.h1>
          <motion.p
            className="mt-2 text-sm leading-relaxed max-w-3xl"
            variants={fadeUp}
            custom={1}
          >
            Tools and technologies I use to build fast, scalable, and
            intelligent systems.
          </motion.p>
        </motion.div>

        {/* ── Skill Groups Grid ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 flex-1 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-slate-400 scrollbar-track-transparent pb-2">
          {skillGroups.map((group, gi) => (
            <motion.div
              key={gi}
              className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/40 dark:bg-white/5 backdrop-blur-sm p-5"
              variants={fadeUp}
              custom={gi + 2}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.15 }}
              whileHover={{ y: -4, transition: { duration: 0.25 } }}
            >
              {/* group label with color accent */}
              <div className="flex items-center gap-2 mb-4">
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: group.color }}
                />
                <span className="text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                  {group.label}
                </span>
              </div>

              {/* skill pills */}
              <div className="flex flex-wrap gap-2">
                {group.skills.map((skill, si) => (
                  <motion.span
                    key={si}
                    className="rounded-full px-3 py-1 text-xs font-medium"
                    style={{
                      backgroundColor: group.color + "18",
                      color: group.color,
                      border: `1px solid ${group.color}33`,
                    }}
                    initial={{ opacity: 0, scale: 0.85 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.05 * si + 0.1 * gi }}
                    viewport={{ once: false }}
                    whileHover={{ scale: 1.08 }}
                  >
                    {skill}
                  </motion.span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* ── CTA ── */}
        <motion.div
          className="shrink-0 mt-5 mb-1"
          variants={fadeUp}
          custom={7}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.2 }}
        >
          <ScrollLink
            section="portfolio"
            className="btn max-w-fit hover:scale-105 transition-transform duration-300"
          >
            View My Work
            <span className="ml-1">
              <BsArrowRight />
            </span>
          </ScrollLink>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default Skills;
